// new brand library -- supports add mid-loading brand sets, better scanning UI,
// better cloning and editing UI, etc.

import { Button, LibraryDialog, LibraryLayout } from '@/ui'
import { PersistenceState } from '@/ui'
import { ConformedBranding } from '@/ui'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BrandLibraryDetails } from './brand-library-details'
import { BrandLibraryList } from './brand-library-list'
import { useBrandLibraryStore } from './brand-library-store'
import { SaveStatus } from './save-status'
import {
	brandLibraryActiveAtom,
	brandLibraryAtom,
	brandLibraryLoadingAtom,
	expandedBrandingIdAtom,
	expandedBrandingStatusAtom,
} from './state'

type BrandLibraryProps = {
	mode: 'static' | 'picker'
	onSelect?: (branding: ConformedBranding) => void
	onClose?: () => void
	showCloseButton?: boolean
	campaignId?: string
}

export function BrandLibrary({
	mode,
	onSelect,
	onClose,
	showCloseButton = true,
	campaignId,
}: BrandLibraryProps) {
	const { t } = useTranslation('common')
	const store = useBrandLibraryStore()
	const brandLibraryLoading = useAtomValue(brandLibraryLoadingAtom, { store })
	const brandLibrary = useAtomValue(brandLibraryAtom, { store })

	const [expandedBrandingId, setExpandedBrandingId] = useAtom(expandedBrandingIdAtom, { store })
	const expandedBrandingStatus = useAtomValue(expandedBrandingStatusAtom, { store })

	const isGenerating = expandedBrandingStatus === 'generating'
	const isEmpty = !brandLibraryLoading && brandLibrary.length === 0

	const handleSelectBranding = () => {
		const branding = brandLibrary.find((b) => b.id === expandedBrandingId)
		if (branding) {
			onSelect?.(branding)
		}
	}

	const [expandedBrandingPersistenceStatus, setExpandedBrandingPersistenceStatus] = useState<PersistenceState>(
		'idle',
	)

	return (
		<LibraryLayout
			title={t('branding.brandLibrary')}
			showCloseButton={showCloseButton}
			onClose={onClose}
			listStorageKey="brand-library"
			defaultListWidth={320}
			minListWidth={240}
			toolbar={!isEmpty
				? (
					<div className="flex items-center gap-3 px-4">
						<SaveStatus status={expandedBrandingPersistenceStatus} />
					</div>
				)
				: undefined}
			committalBar={mode === 'picker' && !isEmpty
				? (
					<div className="flex justify-end p-4">
						<Button
							size="normal"
							type="committal"
							onClick={handleSelectBranding}
							disabled={!expandedBrandingId || isGenerating}
						>
							{t('branding.selectThisBranding')}
						</Button>
					</div>
				)
				: undefined}
			details={isEmpty
				? (
					<div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
						<div className="mb-4">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={48}
								height={48}
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth={1.5}
								strokeLinecap="round"
								strokeLinejoin="round"
								className="text-grey-300"
							>
								<path d="M12 2L2 7l10 5 10-5-10-5z" />
								<path d="M2 17l10 5 10-5" />
								<path d="M2 12l10 5 10-5" />
							</svg>
						</div>
						<h3 className="text-lg font-medium text-grey-800 mb-2">
							{t('branding.emptyTitle')}
						</h3>
						<p className="text-sm text-grey-500 max-w-sm">
							{t('branding.emptyDescription')}
						</p>
					</div>
				)
				: (
					<BrandLibraryDetails
						onStateChange={(status) => setExpandedBrandingPersistenceStatus(status)}
					/>
				)}
			isLoading={brandLibraryLoading}
		>
			<BrandLibraryList campaignId={campaignId} />
		</LibraryLayout>
	)
}

export function BrandLibraryPicker({
	open,
	onSelect,
	onClose,
	campaignId,
}: Omit<BrandLibraryProps, 'mode'> & {
	open: boolean
	onClose: () => void
}) {
	const store = useBrandLibraryStore()
	const setBrandLibraryActive = useSetAtom(brandLibraryActiveAtom, { store })

	useEffect(() => {
		setBrandLibraryActive(open)
	}, [setBrandLibraryActive, open])

	return (
		<LibraryDialog open={open} onClose={onClose}>
			<BrandLibrary
				mode={'picker'}
				onSelect={onSelect}
				onClose={onClose}
				showCloseButton={true}
				campaignId={campaignId}
			/>
		</LibraryDialog>
	)
}

export function StaticBrandLibrary({ onSelect }: Omit<BrandLibraryProps, 'mode'>) {
	const store = useBrandLibraryStore()
	const setBrandLibraryActive = useSetAtom(brandLibraryActiveAtom, { store })

	useEffect(() => {
		setBrandLibraryActive(true)

		return () => {
			setBrandLibraryActive(false)
		}
	}, [setBrandLibraryActive])

	return (
		<div className="ring ring-grey-100 h-full">
			<BrandLibrary
				mode={'static'}
				onSelect={onSelect}
				showCloseButton={false}
			/>
		</div>
	)
}
